/**
 * AURA DIGITAL - THEME CORE JAVASCRIPT
 * Handles Cart Drawer, Accordions, Look Inside Modal, and Global Micro-Interactions
 */

window.AuraTheme = (function () {
  'use strict';

  // Global Mock Cart State for demo & live preview
  let cartState = {
    items: [],
    totalPrice: 0,
    itemCount: 0
  };

  function init() {
    initHeaderScroll();
    initCartDrawer();
    initAccordions();
    initLookInsideModal();
    initCountdowns();
  }

  /* ------------------------------------------------------------------------
     1. Sticky Header Blur Effect on Scroll
     ------------------------------------------------------------------------ */
  function initHeaderScroll() {
    const header = document.querySelector('.site-header');
    if (!header) return;

    window.addEventListener('scroll', () => {
      if (window.scrollY > 20) {
        header.classList.add('is-scrolled');
        header.style.boxShadow = '0 10px 30px -5px rgba(15, 23, 42, 0.08)';
      } else {
        header.classList.remove('is-scrolled');
        header.style.boxShadow = 'none';
      }
    }, { passive: true });
  }

  /* ------------------------------------------------------------------------
     2. Glassmorphic Cart Drawer
     ------------------------------------------------------------------------ */
  function initCartDrawer() {
    const triggers = document.querySelectorAll('[data-cart-trigger]');
    const drawer = document.querySelector('[data-cart-drawer]');
    const overlay = document.querySelector('[data-cart-overlay]');
    const closeBtns = document.querySelectorAll('[data-cart-close]');

    if (!drawer || !overlay) return;

    triggers.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        openCartDrawer();
      });
    });

    closeBtns.forEach(btn => {
      btn.addEventListener('click', closeCartDrawer);
    });

    overlay.addEventListener('click', closeCartDrawer);

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.classList.contains('is-active')) {
        closeCartDrawer();
      }
    });
  }

  function openCartDrawer() {
    const drawer = document.querySelector('[data-cart-drawer]');
    const overlay = document.querySelector('[data-cart-overlay]');
    if (drawer && overlay) {
      drawer.classList.add('is-active');
      overlay.classList.add('is-active');
      document.body.style.overflow = 'hidden';
    }
  }

  function closeCartDrawer() {
    const drawer = document.querySelector('[data-cart-drawer]');
    const overlay = document.querySelector('[data-cart-overlay]');
    if (drawer && overlay) {
      drawer.classList.remove('is-active');
      overlay.classList.remove('is-active');
      document.body.style.overflow = '';
    }
  }

  function addMockBundleToCart(selectedProducts, fixedPrice) {
    const bundleItem = {
      id: `bundle_${Date.now()}`,
      title: 'Custom 6-Planner Digital Bundle',
      price: fixedPrice,
      products: selectedProducts,
      isBundle: true
    };

    cartState.items.push(bundleItem);
    updateCartUI();
    openCartDrawer();
  }

  function addSingleDigitalProduct(product) {
    cartState.items.push(product);
    updateCartUI();
    openCartDrawer();
  }

  function updateCartUI() {
    const countBadge = document.querySelector('[data-cart-count]');
    const itemsContainer = document.querySelector('[data-cart-items]');
    const totalEl = document.querySelector('[data-cart-total]');

    let total = 0;
    let count = 0;

    cartState.items.forEach(item => {
      total += item.price;
      count += (item.isBundle ? 6 : 1);
    });

    if (countBadge) {
      countBadge.textContent = count;
      countBadge.style.display = count > 0 ? 'flex' : 'none';
    }

    if (totalEl) {
      totalEl.textContent = `$${total.toFixed(2)}`;
    }

    if (itemsContainer) {
      if (cartState.items.length === 0) {
        itemsContainer.innerHTML = `
          <div style="text-align: center; padding: 3rem 1rem; color: var(--text-muted);">
            <div style="font-size: 2.5rem; margin-bottom: 0.5rem;">📥</div>
            <h4 style="font-weight: 700; margin-bottom: 0.35rem;">Your digital library is empty</h4>
            <p style="font-size: 0.875rem;">Build a 6-item custom bundle or choose a curated collection.</p>
          </div>
        `;
      } else {
        itemsContainer.innerHTML = cartState.items.map((item, idx) => {
          if (item.isBundle) {
            return `
              <div class="glass-panel" style="padding: 1rem; margin-bottom: 0.75rem; border-color: rgba(37, 99, 235, 0.3);">
                <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 0.5rem;">
                  <div>
                    <span class="badge-pill badge-emerald" style="margin-bottom: 0.35rem;">6-in-1 Custom Pack</span>
                    <h4 style="font-size: 0.95rem; font-weight: 700;">${item.title}</h4>
                    <span style="font-size: 0.75rem; color: var(--emerald-hover); font-weight: 600;">⚡ Instant Download (PDF, GoodNotes)</span>
                  </div>
                  <span style="font-weight: 800; font-size: 1.1rem; color: var(--text-main);">$${item.price.toFixed(2)}</span>
                </div>
                <div style="display: flex; gap: 0.35rem; margin-top: 0.5rem; overflow-x: auto; padding-bottom: 0.25rem;">
                  ${item.products.map(p => `
                    <img src="${p.image}" alt="${p.title}" style="width: 38px; height: 38px; border-radius: 6px; object-fit: cover; border: 1px solid #E2E8F0;" title="${p.title}"/>
                  `).join('')}
                </div>
                <button type="button" onclick="AuraTheme.removeCartItem(${idx})" style="font-size: 0.75rem; color: #EF4444; margin-top: 0.6rem; cursor: pointer; text-decoration: underline;">Remove bundle</button>
              </div>
            `;
          } else {
            return `
              <div class="glass-panel" style="display: flex; gap: 1rem; padding: 1rem; margin-bottom: 0.75rem;">
                <img src="${item.image}" alt="${item.title}" style="width: 55px; height: 55px; border-radius: 8px; object-fit: cover;"/>
                <div style="flex: 1;">
                  <h4 style="font-size: 0.9rem; font-weight: 700;">${item.title}</h4>
                  <span style="font-size: 0.75rem; color: var(--emerald-hover); font-weight: 600;">⚡ Instant Download</span>
                  <div style="display: flex; justify-content: space-between; align-items: center; margin-top: 0.4rem;">
                    <span style="font-weight: 700;">$${item.price.toFixed(2)}</span>
                    <button type="button" onclick="AuraTheme.removeCartItem(${idx})" style="font-size: 0.75rem; color: #EF4444; cursor: pointer;">Remove</button>
                  </div>
                </div>
              </div>
            `;
          }
        }).join('');
      }
    }
  }

  function removeCartItem(index) {
    if (cartState.items[index]) {
      cartState.items.splice(index, 1);
      updateCartUI();
    }
  }

  /* ------------------------------------------------------------------------
     3. Smooth FAQ Accordions
     ------------------------------------------------------------------------ */
  function initAccordions() {
    const triggers = document.querySelectorAll('[data-accordion-trigger]');

    triggers.forEach(btn => {
      btn.addEventListener('click', () => {
        const item = btn.closest('[data-accordion-item]');
        const body = item ? item.querySelector('[data-accordion-body]') : null;
        if (!item || !body) return;

        const isOpen = item.classList.contains('is-open');

        // Close other items in the same container
        const parent = item.parentElement;
        if (parent) {
          parent.querySelectorAll('[data-accordion-item]').forEach(sibling => {
            if (sibling !== item) {
              sibling.classList.remove('is-open');
              const sibBody = sibling.querySelector('[data-accordion-body]');
              if (sibBody) sibBody.style.maxHeight = null;
            }
          });
        }

        if (isOpen) {
          item.classList.remove('is-open');
          body.style.maxHeight = null;
        } else {
          item.classList.add('is-open');
          body.style.maxHeight = body.scrollHeight + 'px';
        }
      });
    });
  }

  /* ------------------------------------------------------------------------
     4. "Look Inside" Digital Flip/Sample Modal
     ------------------------------------------------------------------------ */
  function initLookInsideModal() {
    const triggers = document.querySelectorAll('[data-look-inside-trigger]');
    const modal = document.querySelector('[data-look-inside-modal]');
    const closeBtns = document.querySelectorAll('[data-look-inside-close]');

    if (!modal) return;

    triggers.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        modal.classList.add('is-active');
        document.body.style.overflow = 'hidden';
      });
    });

    closeBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        modal.classList.remove('is-active');
        document.body.style.overflow = '';
      });
    });

    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        modal.classList.remove('is-active');
        document.body.style.overflow = '';
      }
    });
  }

  /* ------------------------------------------------------------------------
     5. Urgency Countdown Timer (Dynamic)
     ------------------------------------------------------------------------ */
  function initCountdowns() {
    const timerEls = document.querySelectorAll('[data-countdown]');
    if (!timerEls.length) return;

    // Set end time 4 hours from now
    const targetTime = new Date().getTime() + (4 * 60 * 60 * 1000) + (18 * 60 * 1000);

    function update() {
      const now = new Date().getTime();
      const diff = targetTime - now;

      if (diff <= 0) return;

      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      timerEls.forEach(el => {
        const hEl = el.querySelector('[data-timer-hours]');
        const mEl = el.querySelector('[data-timer-minutes]');
        const sEl = el.querySelector('[data-timer-seconds]');

        if (hEl) hEl.textContent = String(hours).padStart(2, '0');
        if (mEl) mEl.textContent = String(minutes).padStart(2, '0');
        if (sEl) sEl.textContent = String(seconds).padStart(2, '0');
      });
    }

    update();
    setInterval(update, 1000);
  }

  // Self init
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  return {
    openCartDrawer,
    closeCartDrawer,
    addMockBundleToCart,
    addSingleDigitalProduct,
    removeCartItem
  };
})();
